
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/hooks/useAuth';
import { CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { pb } from '@/lib/pocketbase';
// Removed TutorConsentModal - now handled by UnifiedConsentGuard

interface RegisterFormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  role: 'student' | 'tutor';
  phoneNumber?: string;
  countryCode?: string;
}

interface RegisterFormProps {
  onToggleMode: () => void;
  inviteToken?: string; // Group booking invitation token
}

const RegisterForm = ({ onToggleMode, inviteToken }: RegisterFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showTutorPendingModal, setShowTutorPendingModal] = useState(false);
  const { register, user, refreshUser } = useAuth();
  const navigate = useNavigate();
  
  const form = useForm<RegisterFormData>({
    defaultValues: {
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      role: 'student',
      phoneNumber: '',
      countryCode: '+20'
    }
  });

  const getFieldErrorMessage = (fieldErrors: any) => {
    if (typeof fieldErrors === 'string') return fieldErrors;
    if (fieldErrors?.message) return fieldErrors.message;
    if (fieldErrors?.code === 'validation_not_unique') {
      return 'This email is already registered. Please use a different email or try logging in.';
    }
    if (fieldErrors?.code === 'validation_length_out_of_range') {
      return 'Password must be at least 8 characters long.';
    }
    if (fieldErrors?.code === 'validation_invalid_email') {
      return 'Please enter a valid email address.';
    }
    return 'This field has an error. Please check and try again.';
  };

  const onSubmit = async (data: RegisterFormData) => {
    // Quick validations first
    if (data.password !== data.confirmPassword) {
      form.setError('confirmPassword', {
        type: 'manual',
        message: 'Passwords do not match'
      });
      return;
    }

    if (data.password.length < 8) {
      form.setError('password', {
        type: 'manual',
        message: 'Password must be at least 8 characters long'
      });
      return;
    }

    if (!data.name.trim()) {
      form.setError('name', {
        type: 'manual',
        message: 'Name is required'
      });
      return;
    }

    setIsLoading(true);
    
    // Use setTimeout to prevent blocking the main thread
    setTimeout(async () => {
      try {
        // Combine country code and phone number for tutors
        const fullPhoneNumber = data.role === 'tutor' && data.phoneNumber?.trim() 
          ? `${data.countryCode || '+20'}${data.phoneNumber.trim()}`
          : data.phoneNumber?.trim();
          
        const result = await register(data.email, data.password, data.name.trim(), data.role, fullPhoneNumber);
        
        // If there's an invite token, claim it
        if (inviteToken && pb.authStore.model?.id) {
          try {
            console.log('🎟️ Claiming group booking invite with token:', inviteToken);
            const userId = pb.authStore.model.id;
            console.log('🎟️ User ID:', userId);
            
            // Find the invite by token
            const invites = await pb.collection('group_booking_invites').getFullList({
              filter: `invite_token = "${inviteToken}"`
            });
            
            console.log('🎟️ Found invites:', invites);
            
            if (invites.length > 0) {
              const invite = invites[0];
              console.log('🎟️ Invite details:', invite);
              console.log('🎟️ Invite ID:', invite.id);
              console.log('🎟️ Booking ID:', invite.booking_id);
              console.log('🎟️ Current claimed_by_user_id:', invite.claimed_by_user_id);
              console.log('🎟️ Is already claimed:', invite.is_claimed);
              
              // Update the invite regardless of claimed status if claimed_by_user_id doesn't match
              if (!invite.claimed_by_user_id || invite.claimed_by_user_id === '') {
                console.log('🎟️ Updating invite - claimed_by_user_id is empty');
                const updatedInvite = await pb.collection('group_booking_invites').update(invite.id, {
                  is_claimed: true,
                  claimed_by_user_id: userId
                });
                
                console.log('✅ Group booking invite claimed successfully:', updatedInvite);
                toast.success('Welcome! Your group booking has been linked to your account.');
              } else if (invite.claimed_by_user_id === userId) {
                console.log('ℹ️ Invite already claimed by this user');
                toast.success('Your group booking is already linked to your account.');
              } else {
                console.log('⚠️ Invite claimed by different user:', invite.claimed_by_user_id);
                toast.info('This invite has already been used by another account.');
              }
            } else {
              console.log('⚠️ Invite token not found');
              toast.warning('Invalid or expired invitation link.');
            }
          } catch (inviteError) {
            console.error('❌ Error claiming invite:', inviteError);
            console.error('❌ Error details:', {
              message: inviteError.message,
              status: inviteError.status,
              data: inviteError.data
            });
            // Don't fail registration if invite claim fails
            toast.error('Unable to link group booking. Please contact support.');
          }
        }
        
        // For tutors, UnifiedConsentGuard will handle consent modal automatically
        // For students, show success and navigate
        if (data.role === 'student') {
          if (!inviteToken) {
            toast.success('Account created successfully! Welcome to our platform.');
          }
          const returnAction = localStorage.getItem('returnToAction');
          const redirectTo = returnAction === 'create-success-story' ? '/' : '/lessons';
          navigate(redirectTo);
        } else {
          // For tutors, navigate to profile where UnifiedConsentGuard will show consent
          navigate('/profile');
        }
      } catch (error: any) {
        // Clear previous errors
        form.clearErrors();
        
        // Handle PocketBase validation errors
        if (error?.response?.data) {
          const errorData = error.response.data;
          let hasFieldError = false;
          
          // Handle field-specific errors efficiently
          Object.entries(errorData).forEach(([field, fieldError]) => {
            if (['email', 'password', 'name', 'passwordConfirm'].includes(field)) {
              const errorMessage = getFieldErrorMessage(fieldError);
              const formField = field === 'passwordConfirm' ? 'confirmPassword' : field;
              form.setError(formField as keyof RegisterFormData, {
                type: 'manual',
                message: errorMessage
              });
              hasFieldError = true;
            }
          });
          
          // Show general error if no specific field errors
          if (!hasFieldError) {
            const message = errorData.message || error.message || 'Registration failed. Please check your information and try again.';
            toast.error(message);
          } else {
            toast.error('Please fix the errors below and try again.');
          }
        } else if (error?.message) {
          // Handle network or other errors
          if (error.message.includes('Failed to create record')) {
            toast.error('Registration failed. The email might already be in use or there was a server error. Please try again.');
          } else if (error.message.includes('network') || error.message.includes('fetch')) {
            toast.error('Network error. Please check your internet connection and try again.');
          } else {
            toast.error(error.message);
          }
        } else {
          toast.error('Registration failed. Please check your connection and try again.');
        }
      } finally {
        setIsLoading(false);
      }
    }, 0);
  };

  const handleTutorModalClose = () => {
    setShowTutorPendingModal(false);
    toast.success('Account created successfully! Your tutor application is under review.');
    const returnAction = localStorage.getItem('returnToAction');
    const redirectTo = returnAction === 'create-success-story' ? '/' : '/profile';
    navigate(redirectTo);
  };

  return (
    <>
      <div>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              rules={{
                required: 'Full name is required',
                minLength: {
                  value: 2,
                  message: 'Name must be at least 2 characters long'
                }
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Full Name *</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Enter your full name" 
                      {...field} 
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              rules={{
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Please enter a valid email address'
                }
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email *</FormLabel>
                  <FormControl>
                    <Input 
                      type="email" 
                      placeholder="Enter your email" 
                      {...field} 
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>I am a</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value} disabled={isLoading}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select your role" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="student">Student</SelectItem>
                      <SelectItem value="tutor">Tutor</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {form.watch('role') === 'tutor' && (
              <>
                <FormField
                  control={form.control}
                  name="countryCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Country Code</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value} disabled={isLoading}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select country code" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="+20">🇪🇬 Egypt (+20)</SelectItem>
                          <SelectItem value="+1">🇺🇸 United States (+1)</SelectItem>
                          <SelectItem value="+44">🇬🇧 United Kingdom (+44)</SelectItem>
                          <SelectItem value="+33">🇫🇷 France (+33)</SelectItem>
                          <SelectItem value="+49">🇩🇪 Germany (+49)</SelectItem>
                          <SelectItem value="+39">🇮🇹 Italy (+39)</SelectItem>
                          <SelectItem value="+34">🇪🇸 Spain (+34)</SelectItem>
                          <SelectItem value="+31">🇳🇱 Netherlands (+31)</SelectItem>
                          <SelectItem value="+61">🇦🇺 Australia (+61)</SelectItem>
                          <SelectItem value="+1">🇨🇦 Canada (+1)</SelectItem>
                          <SelectItem value="+91">🇮🇳 India (+91)</SelectItem>
                          <SelectItem value="+86">🇨🇳 China (+86)</SelectItem>
                          <SelectItem value="+81">🇯🇵 Japan (+81)</SelectItem>
                          <SelectItem value="+82">🇰🇷 South Korea (+82)</SelectItem>
                          <SelectItem value="+55">🇧🇷 Brazil (+55)</SelectItem>
                          <SelectItem value="+52">🇲🇽 Mexico (+52)</SelectItem>
                          <SelectItem value="+7">🇷🇺 Russia (+7)</SelectItem>
                          <SelectItem value="+27">🇿🇦 South Africa (+27)</SelectItem>
                          <SelectItem value="+234">🇳🇬 Nigeria (+234)</SelectItem>
                          <SelectItem value="+966">🇸🇦 Saudi Arabia (+966)</SelectItem>
                          <SelectItem value="+971">🇦🇪 UAE (+971)</SelectItem>
                          <SelectItem value="+90">🇹🇷 Turkey (+90)</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone Number</FormLabel>
                      <FormControl>
                        <div className="flex gap-2">
                          <div className="w-20 flex items-center justify-center bg-muted rounded-md border text-sm font-medium">
                            {form.watch('countryCode') || '+20'}
                          </div>
                          <Input 
                            type="tel" 
                            placeholder="Enter your phone number" 
                            {...field} 
                            disabled={isLoading}
                            className="flex-1"
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}
            
            <FormField
              control={form.control}
              name="password"
              rules={{
                required: 'Password is required',
                minLength: {
                  value: 8,
                  message: 'Password must be at least 8 characters long'
                }
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password *</FormLabel>
                  <FormControl>
                    <Input 
                      type="password" 
                      placeholder="Create a password (min. 8 characters)" 
                      {...field} 
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirmPassword"
              rules={{
                required: 'Please confirm your password',
                validate: (value) => {
                  const password = form.getValues('password');
                  return value === password || 'Passwords do not match';
                }
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm Password *</FormLabel>
                  <FormControl>
                    <Input 
                      type="password" 
                      placeholder="Confirm your password" 
                      {...field} 
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>
        </Form>
        
        <div className="mt-4 text-center text-sm">
          Already have an account?{' '}
          <button 
            onClick={onToggleMode}
            className="text-primary hover:underline"
            disabled={isLoading}
          >
            Sign in
          </button>
        </div>
      </div>

      {/* Tutor Consent Modal - Now handled by UnifiedConsentGuard in App.tsx */}

      {/* Tutor Pending Approval Modal */}
      <Dialog open={showTutorPendingModal} onOpenChange={handleTutorModalClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Registration Successful!
            </DialogTitle>
            <DialogDescription className="space-y-3 pt-2">
              <p>
                Thank you for signing up as a tutor! Your registration has been received.
              </p>
              <p>
                You can now access the platform. If auto-approval is enabled, you can immediately create your profile. 
                Otherwise, you'll need to wait for admin approval to access advanced tutor features.
              </p>
              <p className="text-sm text-gray-600">
                Check your status in the dashboard for updates.
              </p>
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end pt-4">
            <Button onClick={handleTutorModalClose}>
              Continue to Dashboard
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RegisterForm;
